export type MetricsMenuChannel =
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'twitter'
  | 'youtube'
  | 'linkedin'
  | 'threads'
  | 'googleplay'
  | 'googlemybusiness'
  | 'allpages';

export const normalizeMetricsChannel = (channel: string): string => channel.toLowerCase();

export const staticMetricsPaths = {
  benchmarks: 'https://benchmarks.stilingue.com.br/analysis?source=MenuMetricsProprietary',
  myConnections: '/metricas-proprietarias/minhas-conexoes',
  conversationsReport: '/metricas-proprietarias/relatorio',
  settings: '/metricas-proprietarias/configuracoes',
} as const;

const BASE = '/metricas-proprietarias';

// Routes mirror the Warroom performance menu (performance_src.html). The selected page is not part of the URL.
const routeTemplates: Record<MetricsMenuChannel, Record<string, string>> = {
  facebook: {
    overview: `${BASE}/facebook/visaogeral`,
    topEngagedUsers: `${BASE}/usuariosmaisengajados/facebook`,
    facebookPageLikesCampaign: `${BASE}/facebook-insights/facebook/pagelikes`,
    fanbaseEvolution: `${BASE}/analise-publicacoes/facebook/fanbase`,
    activePosts: `${BASE}/analise-publicacoes/facebook/publicacoesativadas`,
    paidImpressions: `${BASE}/analise-publicacoes/facebook/impressoespagas`,
    organicImpressions: `${BASE}/analise-publicacoes/facebook/impressoesorganicas`,
    interactionRate: `${BASE}/analise-publicacoes/facebook/taxadeinteracoes`,
    interactionsVolume: `${BASE}/analise-publicacoes/facebook/interactions`,
    useActivityByDayHour: `${BASE}/analise-publicacoes/facebook/atividade-uso-dia-hora`,
    organicVsPaid: `${BASE}/analise-publicacoes/facebook/organicosxpagos`,
    postsVsInteractions: `${BASE}/analise-publicacoes/facebook/postsxinteracoes`,
    reachVsEngagement: `${BASE}/analise-publicacoes/facebook/alcance`,
    commentsSentiment: `${BASE}/analise-comentarios/facebook/sentimento`,
    wordcloud: `${BASE}/analise-comentarios/facebook/wordcloud`,
    posts: `${BASE}/publicacoes/facebook`,
    allComments: `${BASE}/facebook/todoscomentarios`,
    inbox: `${BASE}/facebook/inbox`,
    reviews: `${BASE}/facebook/review`,
    visitorPosts: `${BASE}/facebook/publicacoesvisitantes`,
    compareFanpages: `${BASE}/comparativo/facebook`,
  },
  instagram: {
    overview: `${BASE}/instagram/visaogeral`,
    adsSet: `${BASE}/instagram/conjuntodeanuncios`,
    ads: `${BASE}/instagram/anuncios`,
    topEngagedUsers: `${BASE}/usuariosmaisengajados/instagram`,
    fanbaseEvolution: `${BASE}/analise-publicacoes/instagram/fanbase`,
    postsVsInteractions: `${BASE}/analise-publicacoes/instagram/postsxinteractions`,
    volumeOfInteractions: `${BASE}/analise-publicacoes/instagram/volumeinteracoes`,
    impressionsOverTime: `${BASE}/instagram/analise-publicacoes/impressoes`,
    useActivityByDayHour: `${BASE}/analise-publicacoes/instagram/atividade-uso-dia-hora`,
    reachOverTime: `${BASE}/instagram/analise-publicacoes/alcance`,
    viewsOverTime: `${BASE}/instagram/analise-publicacoes/visualizacoes`,
    interactionsInStories: `${BASE}/instagram/analise-publicacoes/interacoes`,
    commentsSentiment: `${BASE}/analise-comentarios/instagram/sentimentonoscomentarios`,
    wordcloud: `${BASE}/analise-comentarios/instagram/wordcloud`,
    posts: `${BASE}/publicacoes/instagram`,
    stories: '/performance/instagram/stories',
    tagged: '/performance/instagram/marcacoes',
    mentions: '/performance/instagram/mencoes',
    externalComments: '/performance/instagram/mencoes-comentarios',
    commentsInMentions: '/performance/instagram/comentarios-em-mencoes',
    allComments: `${BASE}/instagram/todoscomentarios`,
  },
  tiktok: {
    overview: `${BASE}/TikTok/visaogeral`,
    audience: `${BASE}/TikTok/publico`,
    postsVsInteractions: `${BASE}/analise-publicacoes/TikTok/postsxinteractions`,
    volumeOfInteractions: `${BASE}/analise-publicacoes/TikTok/volumeinteracoes`,
    posts: `${BASE}/publicacoes/TikTok`,
    allComments: `${BASE}/TikTok/todoscomentarios`,
    mentions: `${BASE}/TikTok/publicacoesvisitantes`,
    directMessages: `${BASE}/TikTok/inbox`,
  },
  twitter: {
    overview: `${BASE}/twitter/visaogeral`,
    fanbaseEvolution: `${BASE}/analise-publicacoes/twitter/fanbase`,
    useActivityByDayHour: `${BASE}/analise-publicacoes/twitter/atividade-uso-dia-hora`,
    postsVsInteractions: `${BASE}/analise-publicacoes/twitter/postsxinteractions`,
    volumeOfInteractions: `${BASE}/analise-publicacoes/twitter/volumeinteracoes`,
    posts: `${BASE}/publicacoes/twitter`,
    allComments: `${BASE}/twitter/todoscomentarios`,
    directMessages: `${BASE}/twitter/inbox`,
    mentions: `${BASE}/twitter/publicacoesvisitantes`,
  },
  youtube: {
    overview: `${BASE}/youtube/visaogeral`,
    fanbaseEvolutionSubscribers: `${BASE}/analise-publicacoes/youtube/fanbase`,
    useActivityByDayHour: `${BASE}/analise-publicacoes/youtube/atividade-uso-dia-hora`,
    postsVsInteractions: `${BASE}/analise-publicacoes/youtube/postsxinteractions`,
    volumeOfInteractions: `${BASE}/analise-publicacoes/youtube/volumeinteracoes`,
    commentsSentiment: `${BASE}/analise-comentarios/youtube/sentimentonoscomentarios`,
    wordcloud: `${BASE}/analise-comentarios/youtube/wordcloud`,
    posts: `${BASE}/publicacoes/youtube`,
    allComments: `${BASE}/youtube/todoscomentarios`,
  },
  linkedin: {
    overview: `${BASE}/linkedin/visaogeral`,
    fanbaseEvolution: `${BASE}/analise-publicacoes/linkedin/fanbase`,
    useActivityByDayHour: `${BASE}/analise-publicacoes/linkedin/atividade-uso-dia-hora`,
    postsVsInteractions: `${BASE}/analise-publicacoes/linkedin/postsxinteractions`,
    volumeOfInteractions: `${BASE}/analise-publicacoes/linkedin/volumeinteracoes`,
    commentsSentiment: `${BASE}/analise-comentarios/linkedin/sentimentonoscomentarios`,
    wordcloud: `${BASE}/analise-comentarios/linkedin/wordcloud`,
    allComments: `${BASE}/linkedin/todoscomentarios`,
    posts: `${BASE}/publicacoes/linkedin`,
  },
  threads: {
    overview: `${BASE}/threads/visaogeral`,
    fanbaseEvolution: `${BASE}/analise-publicacoes/threads/fanbase`,
    useActivityByDayHour: `${BASE}/analise-publicacoes/threads/atividade-uso-dia-hora`,
    postsVsInteractions: `${BASE}/analise-publicacoes/threads/postsxinteractions`,
    volumeOfInteractions: `${BASE}/analise-publicacoes/threads/volumeinteracoes`,
    commentsSentiment: `${BASE}/analise-comentarios/threads/sentimentonoscomentarios`,
    wordcloud: `${BASE}/analise-comentarios/threads/wordcloud`,
    posts: `${BASE}/publicacoes/threads`,
    allComments: `${BASE}/threads/todoscomentarios`,
  },
  googleplay: {
    overview: `${BASE}/googleplay/visaogeral`,
    reviewsSentiment: `${BASE}/analise-comentarios/googleplay/sentimento`,
  },
  allpages: {
    overview: `${BASE}/googlemeunegocio/visaogeral`,
  },
  googlemybusiness: {
    overview: `${BASE}/googlemeunegocio/visaogeral`,
    reviewsSentiment: `${BASE}/analise-comentarios/googlemeunegocio/sentimentonoscomentarios`,
  },
};

export const getMetricsMenuItemPath = (
  channel: MetricsMenuChannel,
  itemKey: string,
): string => routeTemplates[channel]?.[itemKey] ?? '';

export interface MetricsPathContext {
  channel: MetricsMenuChannel;
  /** Route path without the trailing page id segment. */
  path: string;
  /** Page id present in the URL right after the route (e.g. /metricas-proprietarias/TikTok/inbox/<pageId>). */
  pageId?: string;
}

const normalizePathname = (pathname: string): string => {
  let end = pathname.length;
  while (end > 0 && pathname[end - 1] === '/') end--;
  return pathname.slice(0, end);
};

const pathChannels = new Map<string, { channel: MetricsMenuChannel; path: string }>();
(Object.entries(routeTemplates) as Array<[MetricsMenuChannel, Record<string, string>]>).forEach(
  ([channel, templates]) => {
    // The all places menu shares its routes with Google My Business.
    if (channel === 'allpages') return;
    Object.values(templates).forEach((path) => pathChannels.set(path.toLowerCase(), { channel, path }));
  },
);

/**
 * Resolves the channel (and optional page id) from the current pathname
 * (e.g. /metricas-proprietarias/publicacoes/facebook or /metricas-proprietarias/TikTok/inbox/<pageId>).
 * Returns undefined when the pathname does not belong to a channel metrics route.
 */
export const resolveMetricsContextFromPath = (pathname: string): MetricsPathContext | undefined => {
  const normalized = normalizePathname(pathname);
  const exact = pathChannels.get(normalized.toLowerCase());
  if (exact) return exact;

  const lastSlash = normalized.lastIndexOf('/');
  const parent = pathChannels.get(normalized.slice(0, lastSlash).toLowerCase());
  const pageId = normalized.slice(lastSlash + 1);
  return parent && pageId ? { ...parent, pageId: decodeURIComponent(pageId) } : undefined;
};
